/**
 * modern-animated-hero-section.jsx
 *
 * Raining-letters + text-scramble primitives, adapted from the 21st.dev
 * component for this CRA / plain-JS / Tailwind codebase.
 *
 * Departures from the original .tsx source, on purpose:
 *   - TypeScript stripped (project is JS) and styled-jsx removed (Next-only;
 *     the `.dud` class lives in styles.css).
 *   - The rain is drawn on a <canvas> instead of 300 absolutely-positioned
 *     spans re-rendered through React state every animation frame. Same look,
 *     none of the layout thrash.
 *   - Honors prefers-reduced-motion (renders a static field) and pauses when
 *     scrolled off-screen or the tab is hidden.
 *
 * Exports:
 *   TextScramble   : raw scramble engine
 *   ScrambleText   : scramble-in-on-view, or cycle through phrases
 *   CharacterRain  : the rain layer, fills its parent
 *   RainingLetters : default export, original full-screen hero composition
 */

import { useEffect, useRef } from "react";

const GLYPHS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";

const randGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];

/* -------------------------------------------------------------------------- */
/* Scramble engine                                                            */
/* -------------------------------------------------------------------------- */

export class TextScramble {
  // `window` caps the random start/end frame range: smaller resolves faster.
  constructor(el, window = 34) {
    this.el = el;
    this.window = window;
    this.chars = "!<>-_\\/[]{}=+*^?#________";
    this.queue = [];
    this.frame = 0;
    this.frameRequest = 0;
    this.resolve = () => {};
    this.update = this.update.bind(this);
  }

  setText(newText) {
    const oldText = this.el.innerText;
    const length = Math.max(oldText.length, newText.length);
    const promise = new Promise((resolve) => (this.resolve = resolve));
    this.queue = [];
    for (let i = 0; i < length; i++) {
      const from = oldText[i] || "";
      const to = newText[i] || "";
      const start = Math.floor(Math.random() * this.window);
      const end = start + Math.floor(Math.random() * this.window);
      this.queue.push({ from, to, start, end });
    }
    cancelAnimationFrame(this.frameRequest);
    this.frame = 0;
    this.update();
    return promise;
  }

  update() {
    let output = "";
    let complete = 0;
    for (let i = 0, n = this.queue.length; i < n; i++) {
      let { from, to, start, end, char } = this.queue[i];
      if (this.frame >= end) {
        complete++;
        output += to;
      } else if (this.frame >= start) {
        if (!char || Math.random() < 0.28) {
          char = this.chars[Math.floor(Math.random() * this.chars.length)];
          this.queue[i].char = char;
        }
        output += `<span class="dud">${char}</span>`;
      } else {
        output += from;
      }
    }
    this.el.innerHTML = output;
    if (complete === this.queue.length) {
      this.resolve();
    } else {
      this.frameRequest = requestAnimationFrame(this.update);
      this.frame++;
    }
  }

  stop() {
    cancelAnimationFrame(this.frameRequest);
  }
}

/* -------------------------------------------------------------------------- */
/* ScrambleText                                                               */
/* -------------------------------------------------------------------------- */

/**
 * <ScrambleText
 *   text="RESOLVES ONCE ON VIEW"       : single-shot mode
 *   phrases={[...]} cycle interval={ms}: carousel mode
 *   as="h2" />
 */
export function ScrambleText({
  text = "",
  phrases,
  cycle = false,
  interval = 2800,
  as: Tag = "span",
  className = "",
  ...rest
}) {
  const elementRef = useRef(null);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const list = phrases && phrases.length ? phrases : [text];

    if (reduced) {
      el.textContent = list[0];
      return;
    }

    const scrambler = new TextScramble(el);

    if (cycle && list.length > 1) {
      let counter = 0;
      let timer;
      let cancelled = false;
      const next = () => {
        if (cancelled) return;
        scrambler.setText(list[counter]).then(() => {
          timer = setTimeout(next, interval);
        });
        counter = (counter + 1) % list.length;
      };
      next();
      return () => {
        cancelled = true;
        clearTimeout(timer);
        scrambler.stop();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            scrambler.setText(list[0]);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      scrambler.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Tag ref={elementRef} className={className} {...rest}>
      {phrases && phrases.length ? phrases[0] : text}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/* CharacterRain: canvas                                                     */
/* -------------------------------------------------------------------------- */

/**
 * <CharacterRain density={1} />
 * Absolutely fills its (positioned) parent. Pointer-events disabled.
 * density scales drop count; 1 ≈ one drop per 16px of width.
 */
export function CharacterRain({ density = 1, className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let raf = 0;
    let visible = true;
    let W = 0;
    let H = 0;
    let drops = [];

    const spawn = () => {
      const count = Math.max(24, Math.floor((W / 16) * density));
      drops = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        v: 0.25 + Math.random() * 1.05,
        size: 9 + Math.random() * 6,
        char: randGlyph(),
        bright: Math.random() < 0.055,
        age: Math.floor(Math.random() * 40),
      }));
    };

    const resize = () => {
      // Fall back to the parent / viewport size if the canvas hasn't been
      // laid out yet (cold mobile load), otherwise the tiny backing store
      // gets CSS-stretched and the glyphs render huge.
      W = canvas.offsetWidth || canvas.parentElement?.offsetWidth || window.innerWidth;
      H = canvas.offsetHeight || canvas.parentElement?.offsetHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.textBaseline = "top";
      spawn();
    };

    const paint = () => {
      ctx.clearRect(0, 0, W, H);
      for (const d of drops) {
        ctx.font = `${d.size}px "JetBrains Mono", ui-monospace, monospace`;
        ctx.fillStyle = d.bright
          ? "rgba(0, 255, 106, 0.80)"
          : "rgba(150, 165, 152, 0.15)";
        ctx.fillText(d.char, d.x, d.y);
      }
    };

    const tick = () => {
      for (const d of drops) {
        d.y += d.v;
        if (++d.age > 36 && Math.random() < 0.06) {
          d.char = randGlyph();
          d.age = 0;
        }
        if (d.y > H + 20) {
          d.y = -20;
          d.x = Math.random() * W;
          d.bright = Math.random() < 0.055;
        }
      }
      paint();
      if (visible) raf = requestAnimationFrame(tick);
    };

    resize();
    // Re-measure after the first layout pass and again once the mono font
    // has loaded — both cases can change the true canvas size on a cold load.
    const rafResize = requestAnimationFrame(resize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => resize());
    }
    window.addEventListener("resize", resize);
    // Catches container size changes the window resize event misses
    // (e.g. the mobile URL bar collapsing after first paint).
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => resize())
        : null;
    ro?.observe(canvas);

    if (reduced) {
      paint();
      return () => {
        cancelAnimationFrame(rafResize);
        window.removeEventListener("resize", resize);
        ro?.disconnect();
      };
    }

    const io = new IntersectionObserver(([entry]) => {
      const nowVisible = entry.isIntersecting && !document.hidden;
      if (nowVisible && !visible) {
        visible = true;
        raf = requestAnimationFrame(tick);
      } else if (!nowVisible) {
        visible = false;
        cancelAnimationFrame(raf);
      }
    });
    io.observe(canvas);

    const onVisibility = () => {
      if (document.hidden) {
        visible = false;
        cancelAnimationFrame(raf);
      } else {
        visible = true;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    raf = requestAnimationFrame(tick);

    return () => {
      visible = false;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(rafResize);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
      io.disconnect();
      ro?.disconnect();
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      aria-hidden="true"
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Original hero composition (default export, API parity with 21st.dev)      */
/* -------------------------------------------------------------------------- */

const RainingLetters = () => (
  <div className="relative h-screen w-full overflow-hidden bg-black">
    <CharacterRain density={1.4} />
    <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
      <ScrambleText
        as="h1"
        phrases={["RAINING LETTERS", "HARIHARASUDHAN", "ACCESS GRANTED"]}
        cycle
        className="font-mono text-6xl font-bold tracking-wider text-white"
      />
    </div>
  </div>
);

export default RainingLetters;
